import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Layers, Key, Shield, Home, Settings, BarChart3, FolderOpen, User, ChevronDown, ChevronLeft, ChevronRight, Menu, X, HelpCircle, LogOut, Headset, History } from 'lucide-react';



import { Link, Outlet, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import useOrganizationId from '../hooks/useOrganizationId';
import ModuleSelector from '../components/layout/ModuleSelector';
import { AICommandBar } from '../components/layout/AICommandBar';
import api from '../axios';
import GenericRequestModal from '../components/common/GenericRequestModal';
import { forceLogout, resolveActiveAccount } from '../utils/authSession';


const ClientDashboard = ({ onLogout }) => {
  const { instance, accounts, inProgress } = useMsal();
  const location = useLocation();

  // 1. Session & User Logic
  const activeAccount = resolveActiveAccount(instance);
  const isCleanPage = location.pathname.includes('/onboarding');

  // Redirect to login if no MSAL account is active
  useEffect(() => {
    if (inProgress === InteractionStatus.None && accounts.length === 0) {
      window.location.href = '/login';
    }
  }, [accounts, inProgress, instance]);

  useEffect(() => {
    if (!activeAccount) return;
    api.post('/session/refresh').catch((error) => {
      if (error.response?.status !== 404) {
        console.error('Session refresh failed:', error);
      }
    });
  }, [activeAccount]);

  const user = useMemo(() => {
    if (!activeAccount) return null;

    const email =
      activeAccount.username ||
      activeAccount.idTokenClaims?.email ||
      activeAccount.idTokenClaims?.preferred_username ||
      activeAccount.name ||
      '';

    return {
      id: activeAccount.localAccountId || activeAccount.homeAccountId,
      email,
      name: email || activeAccount.name || 'User',
      role: 'client',
      organizationName: 'TechCorp Industries', // This could be made dynamic later
      organizationType: 'Client',
      oid: activeAccount.idTokenClaims?.oid,
    };
  }, [activeAccount]);

  // 2. Organization Hook 
  // This hook now handles the 60s focused heartbeat and session invalidation
  const {
    organization,
    organizationId,
    orgLoading,
    orgError,
    modalOpen,
    handleModalSubmit,
    SetupOrganizationModal,
    InvitationSelectionModal
  } = useOrganizationId(user);

  const userWithOrg = useMemo(() => {
    if (!user) return null;
    return {
      ...user,
      organizationName: organization?.name || user.organizationName || 'My Organization'
    };
  }, [user, organization]);

  // 2b. Fetch actual profile username from backend
  const [profileData, setProfileData] = useState({ username: null, role: 'user' });
  const [profileUsername, setProfileUsername] = useState(null); // Keep for compatibility if needed elsewhere, but let's use profileData

  const fetchProfileData = useCallback(async () => {
    try {
      const res = await api.get('/users/me');
      if (res.data) {
        setProfileData({
          username: res.data.username,
          role: res.data.role || 'user'
        });
        if (res.data.username) setProfileUsername(res.data.username);
      }
    } catch (err) {
      // silent - email/default fallback will be used
    }
  }, []);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  // 3. UI State
  const [activeTab, setActiveTab] = useState('home');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);

  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  // 4. Sync Navigation with URL
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/frameworks') || path.startsWith('/assessments')) {
      setActiveTab('frameworks');
    } else if (path === '/') {
      setActiveTab('home');
    } else if (path.startsWith('/documents')) {
      setActiveTab('documents');
    } else if (path.startsWith('/reports')) {
      setActiveTab('reports');
    } else if (path.startsWith('/users')) {
      setActiveTab('users');
    } else if (path.startsWith('/settings')) {
      setActiveTab('settings');
    } else if (path.startsWith('/history')) {
      setActiveTab('history');
    }
  }, [location.pathname]);

  if (!user) return null;

  if (isCleanPage) {
    return (
      <div className="h-screen w-full bg-slate-50 overflow-hidden flex flex-col">
        <main className="flex-1 overflow-hidden flex flex-col">
          <Outlet context={{ user: userWithOrg, organizationId, orgLoading, orgError }} />
        </main>
        <SetupOrganizationModal open={modalOpen} onSubmit={handleModalSubmit} />
        <InvitationSelectionModal />
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-bg-primary">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-md z-[60] lg:hidden animate-in fade-in duration-300"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          flex flex-col border-r border-color-border bg-bg-sidebar shadow-2xl lg:shadow-none transition-all duration-300 z-[70]
          fixed inset-y-0 left-0 w-72 lg:w-auto lg:static lg:inset-auto lg:h-full lg:translate-x-0
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-[260px]'}
        `}
      >
        <div className={`flex items-center justify-between px-6 py-5 flex-none h-20 ${sidebarCollapsed ? 'lg:justify-center lg:px-2' : ''}`}>
          <div className="flex items-center">
            <img
              src={sidebarCollapsed ? "/logo-2.png" : "/logo.png"}
              alt="Proofyx Logo"
              className="h-[38px] w-auto object-contain transition-all duration-300"
            />
          </div>

          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden p-2 rounded-xl hover:bg-slate-50 text-slate-500 hover:text-slate-900 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>


        </div>

        <nav className="flex-1 px-2 py-6 space-y-0.5 overflow-y-auto custom-scrollbar">
          {[
            { to: '/', key: 'home', icon: Home, label: 'Dashboard' },
            { to: '/frameworks', key: 'frameworks', icon: Layers, label: 'Frameworks' },
            { to: '/documents', key: 'documents', icon: FolderOpen, label: 'Document Library' },
            { to: '/reports', key: 'reports', icon: BarChart3, label: 'Compliance Reports' },
            { to: '/users', key: 'users', icon: User, label: 'Access Control' },
            { to: '/history', key: 'history', icon: History, label: 'Activity History' },
          ].map(({ to, key, icon: Icon, label }) => (
            <Link
              key={key}
              to={to}
              onClick={() => {
                setActiveTab(key);
                setMobileMenuOpen(false);
              }}
              className={`
                group flex items-center pl-5 pr-3 py-2.5 text-[13.5px] font-medium rounded-[10px] transition-all duration-200 mb-1 mx-2 relative overflow-hidden
                ${activeTab === key
                  ? 'bg-accent-100 text-accent-700 font-semibold shadow-none before:content-[""] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] before:bg-accent-600 before:rounded-l-[10px]'
                  : 'text-text-secondary hover:bg-bg-row hover:text-text-primary'
                }
                ${sidebarCollapsed ? 'lg:justify-center lg:px-2' : ''}
              `}
            >
              <Icon
                className={`
                  transition-all duration-200 flex-shrink-0
                  ${activeTab === key ? 'text-accent-600' : 'text-text-disabled group-hover:text-text-secondary'}
                  ${sidebarCollapsed ? 'lg:h-6 lg:w-6' : 'h-[18px] w-[18px] mr-3'}
                `}
              />
              <span className={`whitespace-nowrap transition-all duration-300 origin-left ${sidebarCollapsed ? 'lg:hidden opacity-0 w-0' : 'block opacity-100'}`}>
                {label}
              </span>

              {sidebarCollapsed && (
                <div className="hidden lg:block absolute left-full ml-4 px-3 py-2 bg-slate-900 text-white text-[11px] font-semibold uppercase tracking-wide rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[100] shadow-xl">
                  {label}
                </div>
              )}
            </Link>
          ))}

          <div className="pt-8 pb-4">
            <p className={`text-[11px] font-semibold uppercase tracking-wide text-slate-400 px-4 mb-3 ${sidebarCollapsed ? 'hidden' : 'block'}`}>
              System
            </p>
            <Link
              to="/settings"
              onClick={() => {
                setActiveTab('settings');
                setMobileMenuOpen(false);
              }}
              className={`
                group flex items-center pl-5 pr-3 py-2.5 text-[13.5px] font-medium rounded-[10px] transition-all duration-200 mb-1 mx-2 relative overflow-hidden
                ${activeTab === 'settings'
                  ? 'bg-accent-100 text-accent-700 font-semibold shadow-none before:content-[""] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] before:bg-accent-600 before:rounded-l-[10px]'
                  : 'text-text-secondary hover:bg-bg-row hover:text-text-primary'
                }
                ${sidebarCollapsed ? 'lg:justify-center lg:px-2' : ''}
              `}
            >
              <Settings className={`transition-all duration-200 flex-shrink-0 ${activeTab === 'settings' ? 'text-accent-600' : 'text-text-disabled group-hover:text-text-secondary'} ${sidebarCollapsed ? 'lg:h-6 lg:w-6' : 'h-[18px] w-[18px] mr-3'}`} />
              <span className={`whitespace-nowrap transition-all duration-300 origin-left ${sidebarCollapsed ? 'lg:hidden opacity-0 w-0' : 'block opacity-100'}`}>
                Settings
              </span>
            </Link>
          </div>
        </nav>

      </aside>

      {/* Main Container */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header Bar */}
        <header className="bg-white/80 backdrop-blur-xl border-b border-color-border-light px-6 h-[60px] flex flex-none justify-between items-center z-50">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 -ml-2 rounded-xl bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* App Launcher */}
            <ModuleSelector variant="header" user={userWithOrg || user} organizationId={organizationId} />
          </div>

          <AICommandBar />

          <div className="flex items-center gap-2">
            {/* Support */}
            <button 
              onClick={() => setShowSupportModal(true)}
              className="p-2 text-text-disabled hover:text-accent-600 hover:bg-accent-050 rounded-xl transition-all"
              title="Contact Support"
            >
              <Headset className="h-[18px] w-[18px]" />
            </button>




            {/* Divider */}
            <div className="w-px h-7 bg-color-border-light mx-1"></div>
            {/* User Profile */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className={`flex items-center gap-2.5 p-1 rounded-full hover:bg-slate-50 transition-all duration-200 ${showUserMenu ? 'bg-slate-50 ring-2 ring-accent-600/20' : ''}`}
              >
                <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold flex items-center justify-center hover:border-slate-300 transition-colors shadow-sm">
                  {(profileUsername || user.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="hidden md:flex flex-col items-start text-left mr-1">
                  <span className="text-xs font-medium text-slate-700 leading-tight">
                    {profileUsername || userWithOrg.name.split('@')[0]}
                  </span>
                  <span className="text-[10px] text-slate-400 font-normal leading-none mt-0.5 capitalize">
                    {profileData.role || 'Member'}
                  </span>
                </div>
                <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform duration-200 mr-1 hidden md:block ${showUserMenu ? 'rotate-180 text-accent-600' : ''}`} />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl border border-slate-100 shadow-lg shadow-slate-200/40 z-[100] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
                  {/* Profile Header */}
                  <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center font-bold border border-accent-200/50 flex-shrink-0">
                        {(profileUsername || user.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <h3 className="text-sm font-bold text-slate-900 truncate">
                          {profileUsername || userWithOrg.name.split('@')[0]}
                        </h3>
                        <p className="text-xs text-slate-500 truncate" title={userWithOrg.email || userWithOrg.name}>
                          {userWithOrg.email || userWithOrg.name}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Menu Content */}
                  <div className="py-2">
                    <div className="px-5 py-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Current Workspace</p>
                      <div className="flex items-center gap-3 text-sm font-medium text-slate-700 bg-slate-50 px-3 py-2 rounded-lg border border-slate-100">
                        <div className="w-6 h-6 rounded bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-xs flex-shrink-0">
                          {userWithOrg.organizationName.charAt(0).toUpperCase()}
                        </div>
                        <span className="truncate">{userWithOrg.organizationName}</span>
                      </div>
                    </div>

                    <div className="px-5 py-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Access Level</p>
                      <span className="inline-flex items-center px-2 py-1 rounded bg-slate-100 text-slate-600 text-xs font-semibold">
                        <Shield className="w-3 h-3 mr-1.5" />
                        <span className="capitalize">{profileData.role}</span>
                      </span>
                    </div>
                  </div>

                  {/* Sign Out Section */}
                  <div className="p-2 border-t border-slate-100 bg-slate-50/50">
                    <button
                      onClick={async () => {
                        await forceLogout(instance);
                        onLogout?.();
                      }}
                      className="w-full px-3 py-2 text-left text-sm font-semibold text-slate-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors flex items-center gap-2.5 active:scale-[0.98]"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 min-h-0 relative overflow-hidden">
          {/* Subtle background decorative elements */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent-400/3 blur-[120px] rounded-full pointer-events-none -mr-48 -mt-48 z-0"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent-600/3 blur-[120px] rounded-full pointer-events-none -ml-48 -mb-48 z-0"></div>

          <div className="relative z-10 h-full overflow-hidden">
            <Outlet context={{ user, organizationId, orgLoading, orgError }} />
          </div>
        </main>
      </div >

      <SetupOrganizationModal open={modalOpen} onSubmit={handleModalSubmit} />
      <InvitationSelectionModal />
      
      <GenericRequestModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        title="Contact Support"
        subtitle="Need assistance? Our team is here to help you navigate Proofyx."
        initialType="technical-support"
        user={userWithOrg || user}
        organizationId={organizationId}
      />

    </div >

  );
};

export default ClientDashboard;
